import { useMemo, useState } from "react";
import DSBox from "views/system-design/DSBox";
import DSButton from "views/system-design/DSButton";
import DSChip from "views/system-design/DSChip";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSTypography from "views/system-design/DSTypography";
import DSTable from "views/system-design/Table/DSTable";
import DSTableBody from "views/system-design/Table/DSTableBody";
import DSTableCell from "views/system-design/Table/DSTableCell";
import DSTableContainer from "views/system-design/Table/DSTableContainer";
import DSTableHead from "views/system-design/Table/DSTableHead";
import DSTableRow from "views/system-design/Table/DSTableRow";
import { ClockConfig } from "../../../api/clockConfig";
import useClockDetailsByIds from "../../Gifts/GiftOrders/hooks/useClockDetailsByIds";
import {
  useClockConfigOverrides,
  useRemoveClockConfigOverride,
} from "../hooks/useClockConfig";
import ClockConfigOverrideDialog from "./ClockConfigOverrideDialog";

const formatTime = (value?: string) =>
  value ? new Date(value).toLocaleString() : "—";

/** Clocks that deviate from the fleet-wide document, and by which keys. */
const OverridesTab = () => {
  const {
    data: overrides,
    isLoading,
    isFetching,
    isError,
    error,
  } = useClockConfigOverrides();
  const { mutate: remove } = useRemoveClockConfigOverride();

  // The documents are keyed by clock id; resolve the friendly ids so the table reads the
  // same way as the picker.
  const overrideIds = useMemo(
    () => (overrides ?? []).map((entry) => entry.id),
    [overrides],
  );
  const { data: clockDetails } = useClockDetailsByIds(overrideIds);
  const idfById = useMemo(
    () => new Map((clockDetails ?? []).map((clock) => [clock.id, clock.idf])),
    [clockDetails],
  );

  // null = dialog closed, {} = creating, populated = editing that clock.
  const [editing, setEditing] = useState<
    ClockConfig | Record<string, never> | null
  >(null);

  const editingConfig =
    editing && "id" in editing ? (editing as ClockConfig) : null;

  const handleDelete = (clockId: string) => {
    if (
      window.confirm(
        `Remove the config override for "${idfById.get(clockId) ?? clockId}"? The clock goes back to the global values at its next fetch.`,
      )
    ) {
      remove(clockId);
    }
  };

  if (isLoading) {
    return (
      <DSBox sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <DSCircularProgress />
      </DSBox>
    );
  }

  if (isError) {
    return (
      <DSBox sx={{ p: 2 }}>
        <DSTypography color="error">
          Failed to load overrides:{" "}
          {error instanceof Error ? error.message : "Unknown error"}
        </DSTypography>
      </DSBox>
    );
  }

  return (
    <DSBox>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1,
          mb: 2,
        }}
      >
        {isFetching && <DSCircularProgress size={18} />}
        <DSButton
          variant="contained"
          size="small"
          onClick={() => setEditing({})}
        >
          New override
        </DSButton>
      </DSBox>

      {overrides?.length === 0 ? (
        <DSTypography variant="body2" color="text.secondary">
          No clock overrides the global configuration.
        </DSTypography>
      ) : (
        <DSTableContainer>
          <DSTable size="small">
            <DSTableHead>
              <DSTableRow>
                <DSTableCell>Clock</DSTableCell>
                <DSTableCell>Overridden keys</DSTableCell>
                <DSTableCell>Version</DSTableCell>
                <DSTableCell>Updated</DSTableCell>
                <DSTableCell align="right">Actions</DSTableCell>
              </DSTableRow>
            </DSTableHead>
            <DSTableBody>
              {overrides?.map((override) => {
                const keys = Object.keys(override.values ?? {});
                return (
                  <DSTableRow key={override.id} hover>
                    <DSTableCell>
                      <DSTypography variant="body2" fontWeight={500}>
                        {idfById.get(override.id) ?? "—"}
                      </DSTypography>
                      <DSTypography variant="caption" color="text.secondary">
                        {override.id}
                      </DSTypography>
                    </DSTableCell>
                    <DSTableCell>
                      <DSBox
                        sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                      >
                        {keys.length === 0 ? (
                          <DSTypography variant="caption">
                            none (follows global)
                          </DSTypography>
                        ) : (
                          keys.map((key) => (
                            <DSChip
                              key={key}
                              size="small"
                              label={`${key} = ${override.values[key]}`}
                            />
                          ))
                        )}
                      </DSBox>
                    </DSTableCell>
                    <DSTableCell>
                      <DSTypography variant="caption">
                        {override.version}
                      </DSTypography>
                    </DSTableCell>
                    <DSTableCell>
                      <DSTypography variant="caption">
                        {formatTime(override.updatedAt)}
                        {override.updatedBy ? ` · ${override.updatedBy}` : ""}
                      </DSTypography>
                    </DSTableCell>
                    <DSTableCell align="right">
                      <DSBox
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "flex-end",
                        }}
                      >
                        <DSButton
                          size="small"
                          variant="outlined"
                          onClick={() => setEditing(override)}
                        >
                          Edit
                        </DSButton>
                        <DSButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(override.id)}
                        >
                          Remove
                        </DSButton>
                      </DSBox>
                    </DSTableCell>
                  </DSTableRow>
                );
              })}
            </DSTableBody>
          </DSTable>
        </DSTableContainer>
      )}

      <ClockConfigOverrideDialog
        open={!!editing}
        onClose={() => setEditing(null)}
        clockId={editingConfig?.id}
        values={editingConfig?.values}
        // Only relevant when creating: a clock that already has an override is
        // edited from its row, not added again.
        excludeClockIds={
          editingConfig ? [] : (overrides ?? []).map((entry) => entry.id)
        }
      />
    </DSBox>
  );
};

export default OverridesTab;
